import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextInput from '__components/Inputs/TextInput';
import type {
  Automation,
  Folder,
  FolderPayload,
} from 'store/automationsPage/automations.redux';
import { FolderSelect } from './FolderSelect';
import { ModalContentContainer } from './styles';
import { useFolderNameLimit } from './hooks/useFolderNameLimit';
import BasicModal from '__components/Modals/presets/BasicModal';

type EditFolderPayload = {
  id: string;
  name?: string;
  parent_folder_id?: string;
};

type EditFolderModalProps = {
  parentDirectory: Folder;
  folder: Folder;
  folders: Folder[];
  rootFolderId: string;
  onConfirm(data: EditFolderPayload): Promise<void>;
  onHide(): void;
  onAddFolder?(data: FolderPayload): Promise<Folder>;
};

type EditAutomationFolderModalProps = {
  automation: Automation;
  currentDirectory: Folder;
  folders: Folder[];
  validParentFolders: Folder[];
  rootFolderId: string;
  onConfirm(data: { folderId: string }): Promise<void>;
  onHide(): void;
  onAddFolder?(data: FolderPayload): Promise<Folder>;
};

type NewFolderModalProps = {
  currentDirectory: Folder;
  folders: Folder[];
  onHide(): void;
  onConfirm(data: {
    name: string;
    parent_folder_id: string | null;
  }): Promise<void>;
  onAddFolder?(data: FolderPayload): Promise<Folder>;
};

export const EditFolderModal = ({
  parentDirectory,
  folder: folderProp,
  folders,
  rootFolderId,
  onConfirm,
  onAddFolder,
  onHide,
}: EditFolderModalProps) => {
  const { t } = useTranslation();
  const { inputRef, name, handleChangeName } = useFolderNameLimit(
    folderProp.name
  );
  const [folder, setFolder] = useState<Folder | null>(() => {
    if (!parentDirectory.parent_folder_id) {
      return null;
    }
    return folders.find((x) => x.id === parentDirectory.id) ?? null;
  });

  const selectableFolders = useMemo(() => {
    return folders.filter((fldr) => fldr.id !== folderProp.id);
  }, [folders, folderProp.id]);

  return (
    <BasicModal
      show
      heading={t('Edit Folder')}
      disabled={
        !name || (name === folderProp.name && folder?.id === parentDirectory.id)
      }
      onHide={onHide}
      onConfirm={async () => {
        const payload: EditFolderPayload = { id: folderProp.id };
        const parentFolderId = folder ? folder.id : rootFolderId;

        if (folderProp.parent_folder_id !== parentFolderId) {
          payload.parent_folder_id = parentFolderId;
        }

        // There's a bug in the PATCH endpoint - need to always send name
        // if (folderProp.name !== name) {
        //   payload.name = name;
        // }
        payload.name = name;

        await onConfirm(payload);
      }}
      forceFocus={false}
    >
      <ModalContentContainer>
        <TextInput
          inputRef={inputRef}
          label={t('Folder Name')}
          placeholder={t('Enter folder name')}
          value={name}
          onChange={handleChangeName}
        />
        <FolderSelect
          label={t('Parent Folder')}
          chosenFolder={folder}
          validParentFolders={folders}
          folders={selectableFolders}
          onChange={setFolder}
          onAddFolder={onAddFolder}
        />
      </ModalContentContainer>
    </BasicModal>
  );
};

export const EditAutomationFolderModal = ({
  automation,
  currentDirectory,
  folders,
  validParentFolders,
  rootFolderId,
  onConfirm,
  onHide,
  onAddFolder,
}: EditAutomationFolderModalProps) => {
  const { t } = useTranslation();
  const [folder, setFolder] = useState<Folder | null>(currentDirectory);

  return (
    <BasicModal
      show
      heading={t('Edit {{name}} Folder', { name: automation.name })}
      onHide={onHide}
      onConfirm={async () => {
        const folderId = folder ? folder.id : rootFolderId;
        await onConfirm({ folderId });
      }}
      forceFocus={false}
      disabled={folder?.id === currentDirectory?.id}
    >
      <ModalContentContainer>
        <FolderSelect
          chosenFolder={folder}
          folders={folders}
          validParentFolders={validParentFolders}
          onChange={setFolder}
          onAddFolder={onAddFolder}
        />
      </ModalContentContainer>
    </BasicModal>
  );
};

export const NewFolderModal = ({
  currentDirectory,
  folders,
  onConfirm,
  onHide,
  onAddFolder,
}: NewFolderModalProps) => {
  const { t } = useTranslation();
  const { inputRef, name, handleChangeName } = useFolderNameLimit('');
  const [folder, setFolder] = useState<Folder | null>(() => {
    if (!currentDirectory.parent_folder_id) {
      return null;
    }
    return folders.find((x) => x.id === currentDirectory.id) ?? null;
  });

  return (
    <BasicModal
      show
      heading={t('Add Folder')}
      disabled={!name}
      onHide={onHide}
      onConfirm={async () => {
        await onConfirm({ name, parent_folder_id: folder?.id ?? null });
      }}
      forceFocus={false}
    >
      <ModalContentContainer>
        <TextInput
          inputRef={inputRef}
          label={t('Folder Name')}
          placeholder={t('Enter folder name')}
          value={name}
          onChange={handleChangeName}
        />
        <FolderSelect
          label={t('Parent Folder')}
          chosenFolder={folder}
          folders={folders}
          validParentFolders={folders}
          onChange={setFolder}
          onAddFolder={onAddFolder}
        />
      </ModalContentContainer>
    </BasicModal>
  );
};
